import "../styles/packages.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import TopSection from "../components/topSection";
import BG from "../assets/packages_bg1.webp";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { getData } from "../api";

const formatUSD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);

// Friendly, short titles for the card headings (falls back to the API name)
const KIND_TITLES = {
  GARDEN_VISIT: "Visit Only",
  LUNCH: "Lunch",
  HIGH_TEA: "High Tea",
  EVENTS: "Events & Reservations",
};

function Packages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // call your API (no leading slash if your getData already adds /api/v1/)
        const res = await getData(
          "packages?page=1&limit=10&sort=code",
        );
        const list = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : [];
        if (alive) setItems(list);
      } catch (e) {
        console.error(e);
        if (alive) setErr("Failed to load packages.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Map API packages -> UI cards
  const plans = useMemo(() => {
    const sorted = [...items].sort((a, b) =>
      String(a.code).localeCompare(String(b.code)),
    );

    const entranceUsd =
      sorted.find((p) => p.kind === "GARDEN_VISIT")?.priceAdultUsd ?? 0;

    return sorted.map((p, idx) => {
      const isDark = idx % 2 === 1;
      const hasPrice = Number(p.priceAdultUsd) > 0;
      const priceLines = [];
      if (!hasPrice) {
        priceLines.push("Contact us for pricing");
      } else {
        // "Price includes the entrance ticket plus USD 17.50 per person"
        const extra = Number(
          /USD\s*([\d.]+)\s*per person/i.exec(p.note || "")?.[1],
        );
        if (p.kind !== "GARDEN_VISIT" && extra > 0 && entranceUsd > 0) {
          priceLines.push(
            `Foreign: US ${formatUSD(entranceUsd)} + US ${formatUSD(
              extra,
            )} = US ${formatUSD(entranceUsd + extra)}`,
          );
          priceLines.push({ text: "(US$=LKR -CBSL Middle rate of the day)" });
        } else {
          priceLines.push(`Foreign: US ${formatUSD(p.priceAdultUsd)}`);
          if (p.kind === "GARDEN_VISIT") {
            priceLines.push({ text: "(US$=LKR -CBSL Middle rate of the day)" });
          }
        }
      }

      // Events note is split into: Non-Commercial, Commercial, Events (Local / Foreign)
      let noteLines = [];
      if (p.kind === "EVENTS" && p.note) {
        p.note
          .split(/(?=Non-Commercial Shoots:|(?<!Non-)Commercial Shoots:|Events:)/)
          .map((part) => part.trim())
          .filter(Boolean)
          .forEach((part) => {
            if (part.startsWith("Events:")) {
              noteLines.push("Events:");
              part
                .replace("Events:", "")
                .split(/\.\s+(?=Foreign)/)
                .forEach((ev) => noteLines.push(ev.trim()));
            } else {
              noteLines.push(part);
            }
          });
      } else if (p.note) {
        noteLines = [p.note];
      }

      return {
        id: String(idx + 1).padStart(2, "0"),
        title: KIND_TITLES[p.kind] || p.name,
        description: p.description || String(p.kind || "").replaceAll("_", " "),
        priceLines,
        noteLines,
        bg: isDark ? "dark" : "light",
        code: p.code,
      };
    });
  }, [items]);

  if (loading) return <div className="container py-5">Loading packages…</div>;
  if (err) return <div className="container py-5 text-danger">{err}</div>;

  return (
    <>
      <TopSection image={BG} text={"Packages"} backgroundPositionY={"200%"} />
      <section className="light-bg py-5">
        <div className=" container">
          <div className="plan-section">
            <div className="plan-title">PLAN YOUR VISIT</div>
            <div className="plan-description row">
              <p className="col-12 col-lg-6">
                Discover the beauty and legacy of Brief Garden with our
                specially curated visitor packages. Whether you're here for a
                peaceful stroll, a delicious lunch, or a relaxing high tea, each
                experience is designed to let you enjoy the garden's timeless
                charm in your
              </p>
              <p className="col-12 col-lg-6">
                own way. Choose from our thoughtfully designed packages that
                invite you to explore the garden's rich history, enjoy its
                peaceful ambiance, and indulge in relaxing culinary experiences
                surrounded by nature.
              </p>
            </div>
          </div>

          <div className="visit-grid row">
            {plans.map((plan) => (
              <div
                className="col-12 col-sm-6 col-lg-4 p-2"
                key={plan.id /* key on outermost */}
              >
                <div
                  className={`visit-card ${plan.bg === "dark" ? "card-dark" : "card-light"}`}
                >
                  <div className="visit-card-top">
                    <div className="plan-id">{plan.id}</div>
                    <h3 className="text-uppercase">{plan.title}</h3>
                    <p className="description">{plan.description}</p>
                    <div className="price">
                      <p className="price-label">Price:</p>
                      {plan.priceLines.map((line, i) => (
                        <p key={i} className={line.text ? "price-sub" : ""}>
                          {line.text ? (
                            line.text
                          ) : (
                            <>
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                className="check-icon"
                              />{" "}
                              <span>{line}</span>
                            </>
                          )}
                        </p>
                      ))}
                      {plan.noteLines.map((line, i) => (
                        <p className="note" key={`note-${i}`}>
                          {line}
                        </p>
                      ))}
                     
                    </div>
                  </div>

                  <div className="visit-card-bottom">
                   

                    <Link
                      to={`/booking?package=${encodeURIComponent(plan.code)}`}
                      className="card-button"
                    >
                      <button className="book-btn">Book Now</button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!plans.length && (
            <div className="text-center text-muted py-5">
              No packages available right now.
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default Packages;
